import { DefinedUseQueryResult, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import * as yup from 'yup';
import { Currency } from '~/components/Currency/Currency.hooks';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { useSearchParams } from '~/components/Route';

export const schema = yup.object().shape({
  eventId: yup.number().optional(),
});
export const useChallengeEventParams = () => {
  const converter = useCallback((params) => schema.castOrDefault(params), []);
  return useSearchParams<{ eventId?: number }>({ converter });
};

export type ChallengeRewardBoost = {
  citizenBoost: number;
  citizenLv2Count: number;
  citizenLv3Count: number;
  citizenLv4Count: number;
  petBoost: number;
  petCount: number;
  totalBoost: number;
};
export type ChallengeRewardHistory = {
  boost: ChallengeRewardBoost;
  name: string;
  overMinPurchaseAmount: boolean;
  purchaseAmount: number;
  purchaseAmountInUsd: number;
  purchaseCurrency: Currency;
  rewardAmount: number;
  rewardAssetType: 'DON' | 'LUP' | 'UNKNOWN';
  thumbnailUrl: string;
  rewardAt: Date;
};
export type ChallengeRewards = {
  histories: ChallengeRewardHistory[];
  totalPurchaseAmountInUsd: number;
  purchaseRewardScore: number;
  week: number;
};

export const transformChallengeRewards = (data: ChallengeRewards): ChallengeRewards => ({
  ...data,
  histories: data.histories.map((each) => ({
    ...each,
    rewardAt: new Date(each.rewardAt),
  })),
});

export const challengeRewardsUrl = '/api/citizen/v1/challenge/rewards';
export const getChallengeRewards = async (week?: number) => {
  const { data } = await axios.get<ChallengeRewards>(challengeRewardsUrl, { params: { week } });
  return transformChallengeRewards(data);
};

export const useChallengeRewards = (
  week?: number,
  options?: UseDosiminQueryOptions<ChallengeRewards>
) =>
  useQuery<ChallengeRewards>({
    queryKey: ['challenge', 'event', week, 'reward'],
    queryFn: () => getChallengeRewards(week),
    placeholderData: {
      histories: [],
      totalPurchaseAmountInUsd: 0,
      purchaseRewardScore: 0,
      week: week || 0,
    } as ChallengeRewards,
    keepPreviousData: true,
    initialDataUpdatedAt: 0,
    ...options,
  }) as DefinedUseQueryResult<ChallengeRewards>;
